import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { api, money, fmtDate } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TYPES = ["all", "deposit", "transfer", "zelle", "external", "billpay", "card_payment", "trade", "purchase", "adjustment"];
const STATUSES = ["all", "posted", "pending", "rejected"];
const statusCls = { posted: "bg-slate-100 text-slate-600", pending: "bg-amber-50 text-amber-700", rejected: "bg-red-50 text-[var(--red)]" };

const Filter = ({ value, onChange, items, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="w-full sm:w-40" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>{items.map((t) => <SelectItem key={t} value={t}>{t === "all" ? "All" : t.replace("_", " ")}</SelectItem>)}</SelectContent>
  </Select>
);

export default function TxTable({ accountId, cardId, refreshKey, fixedType }) {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ q: "", type: fixedType || "all", status: "all", date_from: "", date_to: "" });
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    const params = { ...f, account_id: accountId, card_id: cardId };
    Object.keys(params).forEach((k) => !params[k] && delete params[k]);
    const t = setTimeout(() => { api.get("/transactions", { params }).then((r) => setRows(r.data)); }, 250);
    return () => clearTimeout(t);
  }, [f, accountId, cardId, refreshKey]);

  return (
    <div className="tile" data-testid="transactions-panel">
      <div className="p-5 flex flex-wrap gap-3 border-b">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className="absolute left-3 top-3 text-slate-400" />
          <Input placeholder="Search transactions" className="pl-9" value={f.q} onChange={(e) => set("q")(e.target.value)} data-testid="tx-search-input" />
        </div>
        {!fixedType && <Filter value={f.type} onChange={set("type")} items={TYPES} testid="tx-type-filter" />}
        <Filter value={f.status} onChange={set("status")} items={STATUSES} testid="tx-status-filter" />
        <Input type="date" className="sm:w-40" value={f.date_from} onChange={(e) => set("date_from")(e.target.value)} data-testid="tx-date-from" />
        <Input type="date" className="sm:w-40" value={f.date_to} onChange={(e) => set("date_to")(e.target.value)} data-testid="tx-date-to" />
      </div>
      <div className="divide-y" data-testid="tx-list">
        {rows.map((t) => (
          <div key={t.id} className="px-5 py-4 flex items-center justify-between gap-4" data-testid={`tx-row-${t.id}`}>
            <div className="min-w-0">
              <div className="font-semibold text-[var(--navy)] truncate">{t.description}</div>
              <div className="text-xs text-slate-500 flex gap-2 items-center mt-0.5">
                {fmtDate(t.date)} · {t.category}
                <span className={`px-2 py-0.5 rounded-full font-semibold ${statusCls[t.status]}`} data-testid="tx-status">
                  {t.status === "pending" ? "Pending verification" : t.status}
                </span>
              </div>
            </div>
            <div className={`font-display font-semibold whitespace-nowrap ${(cardId ? t.amount < 0 : t.amount >= 0) ? "text-emerald-700" : "text-[var(--navy)]"}`}>
              {t.amount >= 0 ? "+" : "-"}{money(Math.abs(t.amount))}
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="p-10 text-center text-slate-500 text-sm" data-testid="tx-empty">No transactions match your filters.</div>}
      </div>
    </div>
  );
}
